import { Fragment, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

const FILE_TYPE_EXTENSIONS = {
  txt: ['.txt'],
  csv: ['.csv'],
  xlsx: ['.xlsx', '.xls'],
  pdf: ['.pdf'],
  docx: ['.docx', '.doc'],
  pptx: ['.pptx', '.ppt'],
  img: ['.jpg', '.jpeg', '.png', '.gif', '.svg'],
};

const FILE_TYPE_LABELS = {
  txt: 'Texto (.txt)',
  csv: 'CSV (.csv)',
  xlsx: 'Excel (.xlsx, .xls)',
  pdf: 'PDF (.pdf)',
  docx: 'Word (.docx, .doc)',
  pptx: 'PowerPoint (.pptx, .ppt)',
  img: 'Imagen (.jpg, .jpeg, .png, .gif, .svg)',
};

export default function FileUploadAnswer({ 
  question, 
  answer, 
  setAnswer, 
  showUploadError,
  instanceId = '',
  hasOwnBold,
}) {
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Extraer configuración de la pregunta
  const allowedTypes = question.options?.map((opt) => opt.optionName) || ['pdf'];
  const maxFileSize = question.score || 5; // en MB
  const maxFiles = question.maxFiles || 1;

  // Archivos actuales
  const currentFiles = answer || [];

  // ✅ Función para validar si hay archivos cuando es requerido
  const hasValidationError = () => {
    return showUploadError && question.required && currentFiles.length === 0;
  };

  const getAcceptedExtensions = () => {
    return allowedTypes
      .flatMap((type) => FILE_TYPE_EXTENSIONS[type] || [])
      .join(',');
  };

  const getFileExtension = (filename) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  };

  const isValidFileType = (filename) => {
    const ext = '.' + getFileExtension(filename);
    return allowedTypes.some((type) =>
      FILE_TYPE_EXTENSIONS[type]?.includes(ext)
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = (files) => {
    setUploadError('');

    const fileArray = Array.from(files);

    // Validar cantidad de archivos
    if (currentFiles.length + fileArray.length > maxFiles) {
      setUploadError(
        `Solo puedes subir un máximo de ${maxFiles} ${
          maxFiles === 1 ? 'archivo' : 'archivos'
        }`
      );
      return;
    }

    const maxSizeBytes = maxFileSize * 1024 * 1024;
    const validFiles = [];
    const errors = [];

    fileArray.forEach((file) => {
      // Validar tipo
      if (!isValidFileType(file.name)) {
        errors.push(`${file.name}: Tipo de archivo no permitido`);
        return;
      }

      // Validar tamaño
      if (file.size > maxSizeBytes) {
        errors.push(
          `${file.name}: Supera el tamaño máximo de ${maxFileSize}MB`
        );
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setUploadError(errors.join('. '));
      return;
    }

    // Agregar archivos válidos
    setAnswer([...currentFiles, ...validFiles]);
  };

  const handleFileInputChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const removeFile = (index) => {
    const updatedFiles = currentFiles.filter((_, i) => i !== index);
    setAnswer(updatedFiles);
    setUploadError('');
  };

  const getAllowedTypesLabel = () => {
    return allowedTypes
      .map((type) => FILE_TYPE_LABELS[type] || type)
      .join(', ');
  };

  return (
    <Fragment>
      <Box sx={{ mb: 3 }}>
        {/* Título y descripción */}
        <Typography variant="h6" sx={{ mb: 1, fontWeight: hasOwnBold(question.questionName) ? 'normal' : 'bold' }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.questionName) }} />
          {question.required && (
            <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
          )}
        </Typography>
        {question.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.description) }} />
          </Typography>
        )}

        {/* Área de carga */}
        <Paper
          variant="outlined"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragging ? '#e3f2fd' : '#fafafa',
            border: isDragging
              ? '2px dashed #1976d2'
              : hasValidationError() // ✅ Mostrar borde rojo si hay error
              ? '2px dashed #d32f2f'
              : '2px dashed #ddd',
            borderRadius: '8px',
            cursor: currentFiles.length >= maxFiles ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: currentFiles.length >= maxFiles ? 0.6 : 1,
          }}
        >
          <UploadFileIcon
            sx={{
              fontSize: 48,
              color: currentFiles.length >= maxFiles ? '#999' : '#1976d2',
              mb: 1,
            }}
          />
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            {currentFiles.length >= maxFiles
              ? `Has alcanzado el límite de ${maxFiles} ${
                  maxFiles === 1 ? 'archivo' : 'archivos'
                }`
              : `Arrastra y suelta ${
                  maxFiles === 1 ? 'tu archivo' : 'tus archivos'
                } aquí`}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            o haz clic para seleccionar
          </Typography>

          <input
            type="file"
            accept={getAcceptedExtensions()}
            multiple={maxFiles > 1}
            hidden
            id={`file-upload-${question.questionId}-${instanceId}`}
            onChange={handleFileInputChange}
            disabled={currentFiles.length >= maxFiles}
          />

          <label htmlFor={`file-upload-${question.questionId}-${instanceId}`}>
            <Button
              component="span"
              variant="contained"
              startIcon={<InsertDriveFileIcon />}
              disabled={currentFiles.length >= maxFiles}
              sx={{ mb: 2 }}
            >
              Seleccionar {maxFiles === 1 ? 'archivo' : 'archivos'}
            </Button>
          </label>

          <Stack spacing={1} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <strong>Archivos permitidos:</strong> {getAllowedTypesLabel()}
            </Alert>
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <strong>Cantidad máxima:</strong> {maxFiles}{' '}
              {maxFiles === 1 ? 'archivo' : 'archivos'}
            </Alert>
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <strong>Tamaño máximo por archivo:</strong> {maxFileSize} MB
            </Alert>
          </Stack>
        </Paper>

        {/* ✅ Error de validación: archivo requerido */}
        {hasValidationError() && (
          <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
            <strong>Campo requerido:</strong> Debes subir al menos un archivo para continuar
          </Alert>
        )}

        {/* Mensajes de error de validación de archivos */}
        {uploadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}

        {/* Lista de archivos cargados */}
        {currentFiles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Archivos cargados ({currentFiles.length}/{maxFiles})
            </Typography>
            <Stack spacing={1}>
              {currentFiles.map((file, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <CheckCircleIcon color="success" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeFile(index)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Fragment>
  );
}